// Ported from qr-phone-frontend src/app/services/network.service.tsx (v3.2.0.0).
// Same request decoration the backend expects (appversion, request_from, language);
// the auth token comes from core/auth instead of the old Redux store.
import { globals } from './globals'
import { URLS } from '../config'
import { getAuthToken } from './auth'
import { backendLanguageCode } from '../i18n'

// Backend message language code. Driven by the active UI language (see src/i18n):
// 1 = English, 3 = Spanish. Kept in one place so it can be made per-user later.
const LANGUAGE_CODE = backendLanguageCode

// FORCED-LOGOUT BACKSTOP. The staff single-device check (backend authenticate)
// rejects a stale staff token with a body carrying action:'logoutPage' (or
// 'logout'). core/http stays framework-agnostic: the staff layer registers a
// handler (see staff/staffAuth) that clears the session and returns to login.
let forcedLogoutHandler: (() => void) | null = null
export const setForcedLogoutHandler = (fn: () => void) => {
  forcedLogoutHandler = fn
}

// TOKEN RENEWAL HOOK. The access token expires, and this app is meant to stay
// open all day waiting for calls, so every authenticated request first makes
// sure the token still has a usable margin, and a 401 is retried once with a
// renewed token before anything gives up. core/http stays framework-agnostic:
// the staff layer registers the renewal (see staff/staffAuth). When no handler
// is registered (the public caller flow, which is anonymous) nothing changes.
let tokenRenewalHandler: (() => Promise<boolean>) | null = null
export const setTokenRenewalHandler = (fn: () => Promise<boolean>) => {
  tokenRenewalHandler = fn
}

const ensureFreshToken = async (): Promise<boolean> => {
  if (!tokenRenewalHandler) return true
  try {
    return await tokenRenewalHandler()
  } catch {
    return false
  }
}

export const httpRequest = async (
  endPoint: string,
  method: string,
  requestData: any = {},
  headers?: any,
  retriedAfterRenewal = false
): Promise<any> => {
  // The query decoration below mutates `endPoint`, so keep the original for a
  // possible retry: repeating with the decorated value would duplicate params.
  const originalEndPoint = endPoint

  try {
    // Renew first when the token is close to expiring, so a request is never
    // sent with a token the API is about to reject.
    if (!retriedAfterRenewal) await ensureFreshToken()
    const token = getAuthToken()

    const params: any = {
      method: method,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }

    if (headers) {
      params.headers = headers
    }

    if ((requestData && method === 'POST') || (requestData && method === 'PATCH')) {
      requestData.appversion = globals.appVersion
      requestData.request_from = globals.request_from
      if (typeof requestData.language === 'undefined') {
        requestData.language = `${LANGUAGE_CODE}`
      }
      params.body = JSON.stringify(requestData)
    }

    if (method === 'GET' && requestData && Object.keys(requestData).length > 0) {
      endPoint +=
        '?' +
        Object.keys(requestData)
          .map((k) => k + '=' + encodeURIComponent(requestData[k]))
          .join('&')
      endPoint += '&appversion=' + globals.appVersion
      endPoint += '&request_from=' + globals.request_from
      if (endPoint.indexOf('language') === -1) {
        endPoint += '&language=' + LANGUAGE_CODE
      }
    }

    if (method === 'GET' && endPoint.indexOf('?') === -1) {
      endPoint +=
        '?appversion=' + globals.appVersion +
        '&request_from=' + globals.request_from +
        '&language=' + LANGUAGE_CODE
    }

    // TIMEOUT THE FETCH REQUEST AFTER X SECONDS.
    const timeout = requestData && requestData?.timeout ? requestData.timeout : 15000
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      console.log(`Request timeout for endpoint: ${endPoint}`, 'error')
      controller.abort()
    }, timeout)
    params.signal = controller.signal

    const response = await fetch(URLS.BASE_URL + '/' + endPoint, params)
    clearTimeout(timeoutId)

    // A single 401 usually means the token expired between the check above and
    // the request reaching the API (a suspended laptop, a throttled background
    // tab). Renew once and repeat before surfacing anything: giving up here
    // drops a staff member out of the app, possibly mid-call.
    if (response.status === 401 && !retriedAfterRenewal && tokenRenewalHandler) {
      const renewed = await ensureFreshToken()
      if (renewed) return httpRequest(originalEndPoint, method, requestData, headers, true)
    }

    try {
      const data = await response.json()
      // SERVER-SIDE FORCED LOGOUT (staff signed in elsewhere / token invalidated).
      const action = data && typeof data === 'object' ? (data as any).action : undefined
      if (action === 'logoutPage' || action === 'logout') {
        try {
          forcedLogoutHandler?.()
        } catch {
          /* ignore */
        }
      }
      return data
    } catch (error) {
      console.error(`API ${endPoint} has an error in converting response to json. ${error}`)
      return { success: false, isNetworkError: true, error: 'Error in network request. ' + error, errorType: 'network' }
    }
  } catch (error) {
    console.log(`API ${endPoint} has an error. ${error}`, 'error')
    return { success: false, isNetworkError: true, error: 'Error in network request. ' + error, errorType: 'network' }
  }
}
